/**
 * useEditExitCoordinator — THE single funnel for every context change that ends a
 * custom-dashboard edit session (spec §2/§5/§7, plan step 14): Done, tab switch, facility
 * switch, and route leave. It guarantees no earlier layout write can commit after the
 * final diff, and gives the user a guaranteed way out of a stuck save (Retry / Discard).
 *
 * Exit sequence (all paths share it):
 *   freeze editing input → cancel the coalesce timer → drain the in-flight tail → enqueue
 *   the final captured-context diff on the SAME queue → await it → only then switch/exit.
 *
 * Failure model (advisor-corrected): freeze covers the ACTIVE drain only. On a failed
 * drain the surface UNFREEZES — the user may Retry, Discard, or simply keep editing (a
 * later successful autosave clears the block per spec §2). A blocked exit is held as an
 * intent: Retry-success or Discard fulfils it; resuming editing cancels it.
 *
 * Factory: one CustomDashboard instance owns one coordinator, wired to its save queue.
 */

import { log as debugLog } from '@/utils/debugLogger';

/**
 * @param {object} deps
 * @param {object} deps.queue                       The layout save queue instance.
 * @param {() => boolean} deps.isEditing            Live edit-mode flag.
 * @param {(v: boolean) => void} deps.setEditing    Enter/leave edit mode.
 * @param {(v: boolean) => void} deps.setExiting    Freeze/unfreeze drag+resize input.
 * @param {() => object} deps.getContext            Captured { facilityId, pageId }.
 * @param {(acked: Array) => void} deps.restoreLayout  Restore acked geometry locally +
 *                                                      force a grid re-render (:key bump).
 * @param {() => Promise<any>} deps.refetchLayout   Best-effort authoritative reload.
 * @param {() => Promise<boolean>} deps.confirmDiscard  $dialog.warning confirm.
 */
export function createEditExitCoordinator({
  queue,
  isEditing,
  setEditing,
  setExiting,
  getContext,
  restoreLayout,
  refetchLayout,
  confirmDiscard,
}) {
  // A blocked exit awaiting resolution. proceed = the caller's context change (Done/tab);
  // resolveExternal = the facility-switch guard's promise resolver. Either may be null.
  let pendingExit = null;

  // True from the moment any exit path starts draining until it cleanly exits, is cancelled,
  // or a blocked save is resolved. Guards against a second exit path (e.g. browser-back while
  // a facility switch is held blocked) starting a concurrent drain or overwriting the held
  // resolveExternal — which would leak the FacilityDropdown/CompanyDropdown await forever.
  let exitInProgress = false;

  async function drain(context) {
    setExiting(true);
    debugLog('[exitCoord] draining layout for exit');
    const ok = await queue.flushForExit(context);
    setExiting(false);
    debugLog('[exitCoord] drain settled — clean:', ok);
    return ok;
  }

  function enterBlocked(intent) {
    pendingExit = intent;
    debugLog('[exitCoord] exit blocked on failed save — awaiting Retry / Discard / resume');
  }

  // Fulfil a held exit intent (Retry-success or Discard): leave edit mode, run the caller's
  // proceed, and resolve a waiting facility-switch guard.
  function fulfilPending() {
    setEditing(false);
    const intent = pendingExit;
    pendingExit = null;
    exitInProgress = false;
    if (!intent) return;
    if (intent.proceed) intent.proceed();
    if (intent.resolveExternal) intent.resolveExternal(true);
  }

  /**
   * Done / tab switch. `proceed` performs the actual context change and runs only when the
   * layout is safely persisted (immediately, or after Retry/Discard). Fire-and-forget.
   * @param {(() => void)|null} proceed
   */
  async function requestExit(proceed) {
    if (!isEditing()) {
      if (proceed) proceed();
      return;
    }
    if (exitInProgress) {
      debugLog('[exitCoord] exit already in progress — ignoring new exit request until resolved');
      return;
    }
    exitInProgress = true;
    const ok = await drain(getContext());
    if (ok) {
      exitInProgress = false;
      setEditing(false);
      if (proceed) proceed();
    } else {
      enterBlocked({ proceed: proceed || null, resolveExternal: null });
    }
  }

  /**
   * Facility switch — the cross-tree handshake FacilityDropdown awaits before location
   * .assign. Resolves true when it is safe to leave (clean drain, or the user resolved the
   * block via Retry/Discard); false when the user cancelled the switch by resuming editing.
   * @returns {Promise<boolean>}
   */
  async function guardExternalNavigation() {
    if (!isEditing()) return true;
    if (exitInProgress) {
      debugLog('[exitCoord] exit already in progress — refusing external navigation until resolved');
      return false;
    }
    exitInProgress = true;
    const ok = await drain(getContext());
    if (ok) {
      exitInProgress = false;
      setEditing(false);
      return true;
    }
    return new Promise(resolve => {
      enterBlocked({ proceed: null, resolveExternal: resolve });
    });
  }

  /**
   * Route leave (beforeRouteLeave / browser back-forward off the route). Synchronous bool:
   * clean → proceed with next(); blocked → next(false) and show the indicator. The nav is
   * NOT held pending (a committed popstate URL can't be suspended cleanly); Retry/Discard
   * resolve the save in place. The tab-id-in-route rework (W5) revisits in-route history.
   * @returns {Promise<boolean>}
   */
  async function drainForRouteLeave() {
    if (!isEditing()) return true;
    if (exitInProgress) {
      debugLog('[exitCoord] exit already in progress — blocking route leave until resolved');
      return false;
    }
    exitInProgress = true;
    const ok = await drain(getContext());
    if (ok) {
      exitInProgress = false;
      setEditing(false);
      return true;
    }
    enterBlocked({ proceed: null, resolveExternal: null });
    return false;
  }

  /** Called on the first move/resize after a blocked exit — cancels the held exit intent. */
  function notifyEditingResumed() {
    if (!pendingExit) return;
    const intent = pendingExit;
    pendingExit = null;
    exitInProgress = false;
    if (intent.resolveExternal) intent.resolveExternal(false); // abort a pending facility switch
    debugLog('[exitCoord] editing resumed — pending exit cancelled');
  }

  /** Retry a failed save. On success, fulfil any held exit; otherwise stay blocked. */
  async function retry() {
    debugLog('[exitCoord] retry requested');
    const ok = await queue.retry(getContext());
    if (ok && pendingExit) fulfilPending();
  }

  /**
   * Discard escape hatch — the guaranteed exit from a stuck save. Restores the last
   * acknowledged layout locally and leaves edit mode WITHOUT any network dependency; the
   * authoritative refetch is best-effort AFTER exit (spec §5).
   */
  async function discard() {
    const confirmed = await confirmDiscard();
    if (!confirmed) return;
    debugLog('[exitCoord] discard confirmed — restoring last saved layout and exiting');
    const acked = queue.discard();
    restoreLayout(acked);
    // Exit + fulfil a held nav unconditionally, before any network call.
    if (pendingExit) {
      fulfilPending();
    } else {
      exitInProgress = false;
      setEditing(false);
    }
    try {
      await refetchLayout();
    } catch (err) {
      debugLog('[exitCoord] post-discard refetch failed — falling to widget error state', err);
    }
  }

  /**
   * Deletion primitive (spec §7 / plan step 14; wired by W5). Deletion stays immediate and
   * OUTSIDE the failed-save block: drop the deleted page's buffered + pending diffs, let any
   * already-issued write settle without its failure blocking the delete, and hand control
   * back with NO final-diff enqueue.
   * @returns {Promise<void>}
   */
  async function prepareForDeletion() {
    debugLog('[exitCoord] prepareForDeletion — dropping diffs, settling in-flight non-blocking');
    setExiting(true);
    const settle = queue.abandonPending();
    await settle;
    setExiting(false);
  }

  return {
    requestExit,
    guardExternalNavigation,
    drainForRouteLeave,
    notifyEditingResumed,
    retry,
    discard,
    prepareForDeletion,
  };
}

export default { createEditExitCoordinator };
